import React, { useState, useCallback, useEffect } from 'react'
import { Upload, Button, Select, Spin } from 'antd'
import {
    InboxOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    TrophyOutlined,
    MessageOutlined,
    WarningOutlined,
    BarChartOutlined,
    BookOutlined,
    ApartmentOutlined,
    CheckCircleOutlined,
    DownOutlined,
    UpOutlined,
    SyncOutlined,
    UnorderedListOutlined,
} from '@ant-design/icons'
import { correctWritingImage } from '../../api/wordcloud'
import { setInsufficientBalanceHandler } from '../../utils/errorHandler'
import VipPurchaseModal from './VipPurchaseModal'
import LoginModal from '../../components/LoginModal'
import styles from './index.module.css'

const { Dragger } = Upload

const GRADE_OPTIONS = [
    { value: 'primary_b1',   label: '小学' },
    { value: 'junior_b1',    label: '初中' },
    { value: 'senior_b1',    label: '高中' },
    { value: 'cet4',         label: '大学英语四级（CET-4）' },
    { value: 'cet6',         label: '大学英语六级（CET-6）' },
    { value: 'graduate_b1',  label: '考研 / 研究生' },
    { value: 'ielts',        label: '雅思（IELTS）' },
    { value: 'toefl',        label: '托福（TOEFL）' },
]

const PHASE = {
    IDLE:       'IDLE',
    SUBMITTING: 'SUBMITTING',
    DONE:       'DONE',
    ERROR:      'ERROR',
}

// ──────────────────────────────────────────────────────────
// 将 rawEssay 按错误位置分段，返回 [{text, error}] 数组
// ──────────────────────────────────────────────────────────
function buildAnnotatedParts(rawEssay, sentsFeedback) {
    if (!rawEssay || !sentsFeedback?.length) return [{ text: rawEssay || '', error: false }]

    const ranges = []
    for (const sent of sentsFeedback) {
        for (const err of sent.errorPosInfos || []) {
            const abs    = (sent.sentStartPos || 0) + (err.startPos || 0)
            const absEnd = (sent.sentStartPos || 0) + (err.endPos   || 0)
            if (absEnd > abs) ranges.push({ start: abs, end: absEnd })
        }
    }

    ranges.sort((a, b) => a.start - b.start)
    const merged = []
    for (const r of ranges) {
        if (merged.length && r.start < merged[merged.length - 1].end) {
            merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, r.end)
        } else {
            merged.push({ ...r })
        }
    }

    const parts = []
    let pos = 0
    for (const { start, end } of merged) {
        if (start > pos) parts.push({ text: rawEssay.slice(pos, start), error: false })
        parts.push({ text: rawEssay.slice(start, end), error: true })
        pos = end
    }
    if (pos < rawEssay.length) parts.push({ text: rawEssay.slice(pos), error: false })
    return parts
}

// 连接词建议文本拼装
function buildConjText(conjAdvice) {
    if (!conjAdvice?.original || !conjAdvice?.fillers) return ''
    const placeholder = conjAdvice.placeholder || '%s'
    let text = conjAdvice.original
    for (const filler of conjAdvice.fillers) {
        text = text.replace(placeholder, Array.isArray(filler) ? filler.join('、') : String(filler))
    }
    return text
}

// ──────────────────────────────────────────────────────────
// Shared section title
// ──────────────────────────────────────────────────────────
function SectionTitle({ icon, title }) {
    return (
        <div className={styles.sectionTitleRow}>
            <span className={styles.sectionTitleBar} />
            <span className={styles.sectionTitleIcon}>{icon}</span>
            <span className={styles.sectionTitleText}>{title}</span>
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 综合评分卡片
// ──────────────────────────────────────────────────────────
function ScoreCard({ result }) {
    const totalScore = result.totalScore ?? result.essayReport?.totalScore ?? 0
    const essayAdvice = result.essayAdvice || result.essayReport?.essayAdvice || ''
    const major = result.majorScore || result.essayReport?.majorScore || {}

    const subScores = [
        { label: '词汇得分', value: major.wordScore },
        { label: '语法得分', value: major.grammarScore },
        { label: '逻辑得分', value: major.structureScore },
        { label: '内容得分', value: major.topicScore },
    ].filter(s => s.value != null)

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<TrophyOutlined />} title="综合评价" />
            {essayAdvice && <p className={styles.essayAdvice}>{essayAdvice}</p>}
            <div className={styles.scoreRow}>
                <div className={styles.scoreBox}>
                    <span className={styles.scoreBoxLabel}>综合得分</span>
                    <span className={styles.scoreNumber}>{totalScore}</span>
                </div>
                {subScores.length > 0 && (
                    <div className={styles.subScoreList}>
                        {subScores.map((s, i) => (
                            <div key={i} className={styles.subScoreItem}>
                                <span className={styles.subScoreLabel}>{s.label}</span>
                                <span className={styles.subScoreValue}>{s.value} 分</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 点评卡片
// ──────────────────────────────────────────────────────────
function CommentCard({ result }) {
    const major = result.majorScore || result.essayReport?.majorScore || {}
    const lines = [
        { title: '语法', text: major.grammarAdvice },
        { title: '词汇', text: major.wordAdvice },
        { title: '结构', text: major.structureAdvice },
    ].filter(l => l.text)

    if (!lines.length) return null

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<MessageOutlined />} title="点评" />
            {lines.map((l, i) => (
                <div key={i} className={styles.commentLine}>
                    <span className={styles.commentBullet}>{l.title}：</span>
                    <span className={styles.commentText}>{l.text}</span>
                </div>
            ))}
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 语法错误建议
// ──────────────────────────────────────────────────────────
function GrammarAdviceCard({ result }) {
    const gea = (result.essayReport || {}).grammarErrorAdvice
    if (!gea) return null

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<WarningOutlined />} title="语法错误建议" />
            {gea.advice && (
                <div className={styles.commentLine}>
                    <span className={styles.commentBullet}>建议：</span>
                    <span className={styles.commentText}>{gea.advice}</span>
                </div>
            )}
            {(gea.evaluation || gea.highFreqErrors?.length > 0) && (
                <div className={styles.commentLine}>
                    <span className={styles.commentBullet}>评价：</span>
                    <span className={styles.commentText}>
                        {gea.evaluation}{gea.highFreqErrors?.length > 0 ? gea.highFreqErrors.join('、') : ''}
                    </span>
                </div>
            )}
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 带错误标注的作文原文
// ──────────────────────────────────────────────────────────
function AnnotatedEssay({ rawEssay, sentsFeedback }) {
    const parts = buildAnnotatedParts(rawEssay, sentsFeedback)
    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<FileTextOutlined />} title="作文原文（错误词已标红）" />
            <p className={styles.essayBody}>
                {parts.map((p, i) =>
                    p.error
                        ? <span key={i} className={styles.errorWord}>{p.text}</span>
                        : <span key={i}>{p.text}</span>
                )}
            </p>
            <span className={styles.charCount}>{rawEssay?.length || 0} / 5000</span>
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 单句错误详情（可展开）
// ──────────────────────────────────────────────────────────
function SentenceItem({ sent, index }) {
    const [expanded, setExpanded] = useState(false)
    const hasErrors = sent.errorPosInfos?.length > 0

    return (
        <div
            className={`${styles.sentItem} ${hasErrors ? styles.sentItemClickable : ''}`}
            onClick={hasErrors ? () => setExpanded(v => !v) : undefined}
        >
            <div className={styles.sentRow}>
                <span className={styles.sentLabel}>原句 {index + 1}</span>
                <span className={styles.sentText}>{sent.rawSent}</span>
                {!hasErrors && <CheckCircleOutlined className={styles.sentOk} />}
            </div>

            {hasErrors && (
                <>
                    <div className={styles.sentDivider} />
                    <div className={styles.sentRow}>
                        <span className={styles.sentLabel}>错误原因</span>
                        <span className={styles.sentFeedbackText}>{sent.sentFeedback}</span>
                    </div>

                    {expanded && sent.errorPosInfos?.map((err, ei) => (
                        <div key={ei} className={styles.errDetail}>
                            <div className={styles.errDetailTitle}>
                                <span className={styles.errChipRed}>{err.orgChunk || '—'}</span>
                                <span className={styles.errTypeText}>{err.errorTypeTitle}</span>
                            </div>
                            {err.errBaseInfo && (
                                <p className={styles.errBaseInfo}>{err.errBaseInfo}</p>
                            )}
                            {err.target?.length > 0 && (
                                <div className={styles.targetRow}>
                                    <span className={styles.targetLabel}>建议替换：</span>
                                    {err.target.map((t, ti) => (
                                        <span key={ti} className={styles.targetChip}>{t}</span>
                                    ))}
                                </div>
                            )}
                            {err.exampleCases?.length > 0 && (
                                <div className={styles.exampleWrap}>
                                    <span className={styles.exampleTitle}>示例：</span>
                                    {err.exampleCases.map((ex, xi) => (
                                        <div key={xi} className={styles.exampleCase}>
                                            <div className={styles.exampleRow}>
                                                <span className={styles.badgeWrong}>✗</span>
                                                <span className={styles.exampleWrongText}>{ex.error}</span>
                                            </div>
                                            <div className={styles.exampleRow}>
                                                <span className={styles.badgeRight}>✓</span>
                                                <span className={styles.exampleRightText}>{ex.right}</span>
                                            </div>
                                            {ex.rightTranslate && (
                                                <p className={styles.exampleTranslate}>{ex.rightTranslate}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}

                    <div className={styles.expandHint}>
                        {expanded ? <UpOutlined /> : <DownOutlined />}
                    </div>
                </>
            )}
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 逐句批改列表
// ──────────────────────────────────────────────────────────
function SentenceFeedbackList({ sentsFeedback }) {
    if (!sentsFeedback?.length) return null
    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<UnorderedListOutlined />} title="逐句批改详情" />
            {sentsFeedback.map((sent, i) => (
                <SentenceItem key={i} sent={sent} index={i} />
            ))}
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 逻辑连接词分布
// ──────────────────────────────────────────────────────────
function ConjCard({ result }) {
    const report = result.essayReport || {}
    const conjTypeInfos = report.conjTypeInfos || []
    const conjText = buildConjText(report.conjAdvice)

    if (!conjTypeInfos.length && !conjText) return null

    const maxCount = Math.max(...conjTypeInfos.map(c => c.count), 1)

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<BarChartOutlined />} title="逻辑连接词分布" />
            {conjText && <p className={styles.adviceText}>{conjText}</p>}
            {conjTypeInfos.length > 0 && (
                <div className={styles.barList}>
                    {conjTypeInfos.map((c, i) => (
                        <div key={i} className={styles.barItem}>
                            <span className={styles.barLabel}>{c.name}</span>
                            <div className={styles.barTrack}>
                                <div
                                    className={styles.barFill}
                                    style={{ width: `${Math.round((c.count / maxCount) * 100)}%` }}
                                />
                            </div>
                            <span className={styles.barCount}>{c.count} 次</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 词汇水平分布
// ──────────────────────────────────────────────────────────
function LexicalCard({ result }) {
    const lex = (result.essayReport || {}).lexicalDistribution
    if (!lex) return null

    const adviceText = lex.advice?.replace(/<[^>]+>/g, '') || ''
    const levels = [
        { name: '小学', num: lex.primaryWordNum      || 0, pct: lex.primaryWordNumPercent      || 0 },
        { name: '初中', num: lex.juniorWordNum        || 0, pct: lex.juniorWordNumPercent        || 0 },
        { name: '高中', num: lex.seniorWordNum        || 0, pct: lex.seniorWordNumPercent        || 0 },
        { name: '四级', num: lex.cet4WordNum          || 0, pct: lex.cet4WordNumPercent          || 0 },
        { name: '六级', num: lex.cet6WordNum          || 0, pct: lex.cet6WordNumPercent          || 0 },
        { name: '托雅', num: lex.toeflAndIeltsWordNum || 0, pct: lex.toeflAndIeltsWordNumPercent || 0 },
    ]
    const maxPct = Math.max(...levels.map(l => l.pct), 0.001)

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<BookOutlined />} title="词汇水平分布" />
            {adviceText && <p className={styles.adviceText}>{adviceText}</p>}
            <div className={styles.barList}>
                {levels.map((l, i) => (
                    <div key={i} className={styles.barItem}>
                        <span className={styles.barLabel}>{l.name}</span>
                        <div className={styles.barTrack}>
                            <div
                                className={styles.barFill}
                                style={{ width: `${Math.round((l.pct / maxPct) * 100)}%` }}
                            />
                        </div>
                        <span className={styles.barCount}>{l.num} 词</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// 句子结构分析
// ──────────────────────────────────────────────────────────
function SentComplexCard({ result }) {
    const info = (result.essayReport || {}).sentComplexInfo
    if (!info) return null

    const simplePct  = Math.round((info.simpleSentPercent  || 0) * 100)
    const complexPct = Math.round((info.complexSentPercent || 0) * 100)

    return (
        <div className={styles.sectionCard}>
            <SectionTitle icon={<ApartmentOutlined />} title="句子结构分析" />
            {info.sentenceStructureAdvice && (
                <p className={styles.adviceText}>{info.sentenceStructureAdvice}</p>
            )}
            <div className={styles.sentComplexRow}>
                <div className={styles.sentComplexItem}>
                    <span className={styles.sentComplexNum}>{info.simpleSentNum}</span>
                    <span className={styles.sentComplexLabel}>简单句</span>
                    <span className={styles.sentComplexPct}>{simplePct}%</span>
                </div>
                <div className={styles.sentComplexDivider} />
                <div className={styles.sentComplexItem}>
                    <span className={styles.sentComplexNum}>{info.complexSentNum}</span>
                    <span className={styles.sentComplexLabel}>复杂句</span>
                    <span className={styles.sentComplexPct}>{complexPct}%</span>
                </div>
            </div>
        </div>
    )
}

// ──────────────────────────────────────────────────────────
// Main Page
// ──────────────────────────────────────────────────────────
export default function EssayCorrectionPage() {
    const [phase,       setPhase]       = useState(PHASE.IDLE)
    const [file,        setFile]        = useState(null)
    const [previewUrl,  setPreviewUrl]  = useState(null)
    const [grade,       setGrade]       = useState('graduate_b1')
    const [apiResponse, setApiResponse] = useState(null)
    const [errorMsg,    setErrorMsg]    = useState('')
    const [vipModalOpen, setVipModalOpen] = useState(false)
    const [loginModalOpen, setLoginModalOpen] = useState(false)

    const isLoggedIn = !!localStorage.getItem('token')

    // 设置余额不足处理器
    useEffect(() => {
        const handler = (msg) => {
            setVipModalOpen(true)
        }
        setInsufficientBalanceHandler(handler)
        return () => setInsufficientBalanceHandler(null)
    }, [])

    // 支付成功回调
    const handleVipSuccess = useCallback(() => {
        setVipModalOpen(false)
    }, [])

    const clearFile = useCallback(() => {
        setFile(null)
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
    }, [previewUrl])

    const draggerProps = {
        accept: 'image/*',
        multiple: false,
        showUploadList: false,
        beforeUpload: (f) => {
            if (previewUrl) URL.revokeObjectURL(previewUrl)
            setFile(f)
            setPreviewUrl(URL.createObjectURL(f))
            setApiResponse(null)
            setErrorMsg('')
            setPhase(PHASE.IDLE)
            return false
        },
    }

    const handleRemoveFile = useCallback(() => {
        clearFile()
        setApiResponse(null)
        setErrorMsg('')
        setPhase(PHASE.IDLE)
    }, [clearFile])

    const doSubmit = useCallback(async () => {
        if (!file) return
        setPhase(PHASE.SUBMITTING)
        setErrorMsg('')
        try {
            const resp = await correctWritingImage(file, grade)
            setApiResponse(resp)
            setPhase(PHASE.DONE)
        } catch (err) {
            // 8011 错误会通过 setInsufficientBalanceHandler 跳转到充值页，不需要显示错误
            if (err.errCode !== 8011) {
                setErrorMsg(err.message || '批改失败，请重试')
                setPhase(PHASE.ERROR)
            } else {
                // 重置为 IDLE 状态
                setPhase(PHASE.IDLE)
            }
        }
    }, [file, grade])

    const handleSubmit = useCallback(() => {
        if (!file) return
        if (!isLoggedIn) {
            setLoginModalOpen(true)
            return
        }
        doSubmit()
    }, [file, isLoggedIn, doSubmit])

    const handleReset = useCallback(() => {
        clearFile()
        setApiResponse(null)
        setErrorMsg('')
        setPhase(PHASE.IDLE)
    }, [clearFile])

    const isLoading  = phase === PHASE.SUBMITTING
    const result     = apiResponse?.data?.Result || null
    const sentsFeedback = result?.essayFeedback?.sentsFeedback || []

    return (
        <div className={styles.pageWrapper}>
            <LoginModal
                open={loginModalOpen}
                onCancel={() => setLoginModalOpen(false)}
                continueAction={doSubmit}
            />
            <div className={styles.layout}>

                {/* ── Left Panel ── */}
                <div className={styles.leftPanel}>
                    <div className={styles.sectionHeader}>
                        <FileTextOutlined className={styles.sectionIcon} />
                        <span>上传作文图片</span>
                    </div>

                    {!file ? (
                        <Dragger {...draggerProps} className={styles.uploadDragger}>
                            <div className={styles.uploadPrompt}>
                                <InboxOutlined style={{ fontSize: 32, color: '#1677ff' }} />
                                <p className={styles.uploadText}>点击或拖拽上传</p>
                                <p className={styles.uploadHint}>支持 JPG · PNG · WEBP · BMP</p>
                            </div>
                        </Dragger>
                    ) : (
                        <div className={styles.fileDisplay}>
                            {previewUrl && (
                                <img src={previewUrl} alt="preview" className={styles.previewThumb} />
                            )}
                            <span className={styles.fileName}>{file.name}</span>
                            <CloseCircleOutlined
                                className={styles.fileRemove}
                                onClick={handleRemoveFile}
                            />
                        </div>
                    )}

                    <div className={styles.sectionHeader} style={{ marginTop: 8 }}>
                        <span>学段 / 难度</span>
                    </div>
                    <Select
                        value={grade}
                        onChange={setGrade}
                        options={GRADE_OPTIONS}
                        style={{ width: '100%' }}
                    />

                    <Button
                        type="primary"
                        className={styles.analyzeBtn}
                        onClick={handleSubmit}
                        disabled={!file || isLoading}
                        loading={isLoading}
                    >
                        {isLoading ? '批改中…' : '开始批改'}
                    </Button>

                    {(phase === PHASE.DONE || phase === PHASE.ERROR) && (
                        <Button
                            className={styles.resetBtn}
                            onClick={handleReset}
                            icon={<SyncOutlined />}
                        >
                            重新批改
                        </Button>
                    )}
                </div>

                {/* ── Right Panel ── */}
                <div className={styles.rightPanel}>
                    {phase === PHASE.IDLE && (
                        <div className={styles.emptyState}>
                            <FileTextOutlined className={styles.emptyIcon} />
                            <p className={styles.emptyText}>
                                上传作文图片后，选择学段，点击「开始批改」获取批改结果
                            </p>
                        </div>
                    )}

                    {phase === PHASE.SUBMITTING && (
                        <div className={styles.loadingBox}>
                            <Spin size="large" />
                            <p className={styles.loadingText}>AI 正在批改中，请稍候…</p>
                        </div>
                    )}

                    {phase === PHASE.ERROR && (
                        <div className={styles.errorBox}>
                            <WarningOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
                            <p className={styles.errorText}>{errorMsg}</p>
                        </div>
                    )}

                    {phase === PHASE.DONE && result && (
                        <div className={styles.rightInner}>
                            <ScoreCard result={result} />
                            <CommentCard result={result} />
                            <GrammarAdviceCard result={result} />
                            {result.rawEssay && (
                                <AnnotatedEssay
                                    rawEssay={result.rawEssay}
                                    sentsFeedback={sentsFeedback}
                                />
                            )}
                            <SentenceFeedbackList sentsFeedback={sentsFeedback} />
                            <ConjCard result={result} />
                            <LexicalCard result={result} />
                            <SentComplexCard result={result} />
                        </div>
                    )}
                </div>
            </div>

            {/* VIP 购买弹窗 */}
            <VipPurchaseModal
                open={vipModalOpen}
                onClose={() => setVipModalOpen(false)}
                onSuccess={handleVipSuccess}
            />
        </div>
    )
}
